import { useTranslation } from "react-i18next";
import Role from "enums/Role";
import { useFeatureEntitlements } from "@frontegg/react";
import {
  AnalyticsUpIcon,
  BankIcon,
  Contact01Icon,
  CreditCardIcon,
  DashboardSquare01Icon,
  Invoice03Icon,
  InvoiceIcon,
  LibraryIcon,
  MoneySend01Icon,
  Settings02Icon,
  TransactionIcon,
  UserMultiple02Icon,
  Wallet01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { useCheckUserRole } from "./useCheckUserRole";
import { useAppEntitlements } from "./useAppEntitlements";
import { useMemo } from "react";
import useShowReimbursements from "hooks/useShowReimbursements";
import type {
  NavItemConfig,
  SubNavItemConfig,
} from "components/nav/AppSidebar";
import useShowRewards from "hooks/useShowRewards";
import useCanTakePayments from "hooks/useCanTakePayments";

export function useSidebarItems(): {
  sidebarItems: NavItemConfig[];
  subSideBarItems: SubNavItemConfig[];
} {
  const { t } = useTranslation();

  const isAuditorOrHigher = useCheckUserRole(Role.Auditor);
  const isManagerOrHigher = useCheckUserRole(Role.Manager);
  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });
  const isAccountant = useCheckUserRole(Role.Accountant, { exact: true });

  const isAuditorOrAccountant = useMemo(() => {
    return isAccountant || isAuditor;
  }, [isAccountant, isAuditor]);

  const { byoc, byocOnly } = useAppEntitlements();
  const { isEntitled: featureInvoices } =
    useFeatureEntitlements("feature_invoices");
  const { isEntitled: featurePaymentLinks } = useFeatureEntitlements(
    "feature_payment_links",
  );
  const { isEntitled: featureSubscriptions } = useFeatureEntitlements(
    "feature_subscriptions",
  );
  const { isEntitled: featureContacts } =
    useFeatureEntitlements("feature_contacts");
  const { isEntitled: featureNotification } = useFeatureEntitlements(
    "feature_notifications",
  );
  const { isEntitled: featureLocation } =
    useFeatureEntitlements("feature_locations");
  const { isEntitled: featureAllocations } = useFeatureEntitlements(
    "feature_allocations",
  );
  const { isEntitled: featureTransactionPolicies } = useFeatureEntitlements(
    "feature_transaction_policies",
  );
  const { isEntitled: customColorFF } = useFeatureEntitlements(
    "feature_company_custom_brand",
  );
  const { isEntitled: featurePlatformLogoCompanySettings } =
    useFeatureEntitlements("feature_platform_logo_company_settings");
  // Same pair CompanySettings gated the branding tab on, kept together so the
  // nav entry and the route cannot disagree about who sees branding.
  const showBrandingAndColors =
    customColorFF && featurePlatformLogoCompanySettings;
  const { isEntitled: featureApiAccess } =
    useFeatureEntitlements("feature_api_access");
  const { isEntitled: featureCustomFields } = useFeatureEntitlements(
    "feature_card_custom_fields",
  );
  const { isEntitled: featurePayments } =
    useFeatureEntitlements("feature_payments");
  const { isEntitled: featureStatements } =
    useFeatureEntitlements("feature_statements");

  const { isEntitled: featureIntegrations } = useFeatureEntitlements(
    "feature_integrations",
  );

  const showReimbursements = useShowReimbursements();

  // Payment links and subscriptions only ask customers to pay, so they wait on the company being able to take payments.
  const canTakePayments = useCanTakePayments();
  const showPaymentLinks = featurePaymentLinks && canTakePayments;
  const showSubscriptions = featureSubscriptions && canTakePayments;
  const anyGetPaidFeature =
    featureInvoices || showPaymentLinks || showSubscriptions;

  const showRewards = useShowRewards();

  /**
   * A BYOC-only company gets a trimmed menu: Dashboard, Transactions, Accounts,
   * Users, Analytics, Settings.
   *
   * Most of what goes is wallet-specific and would be empty for them — Payments,
   * Get Paid, Cards, Statements, Contacts, and Reimbursements, which is
   * out-of-pocket spend on the Outpave side. Receipt Library does hold BYOC data,
   * and is hidden because the agreed menu says so rather than because there is
   * nothing behind it.
   *
   * Hidden, not blocked. Every route still resolves for anyone with a link or the
   * URL — this is menu scoping, not authorization.
   */
  const showSpendMenus = !byocOnly;

  const sidebarItems: NavItemConfig[] = [
    {
      IconComponent: DashboardSquare01Icon,
      name: "Dashboard",
      url: "dashboard",
      isVisible: isAuditorOrHigher,
    },
    {
      IconComponent: TransactionIcon,
      name: "Transactions",
      url: "transactions",
    },
    {
      IconComponent: Wallet01Icon,
      name: "Reimbursements",
      url: "reimbursements",
      isVisible: showSpendMenus && showReimbursements,
    },
    {
      /**
       * Accounts is the BYOC-only home for the Linked Accounts page, which otherwise
       * lives under Settings. Same page, promoted to the top level because linked
       * cards are the spine of the product for these companies rather than a setting.
       *
       * The audience is deliberately narrower than `byocOnly`. It mirrors the Settings
       * sub-item this replaces, which excludes Auditor and Accountant, and it also
       * excludes Manager: ManagerRoutes has no `accounts` route, so a Manager would
       * otherwise get a menu entry that renders an empty outlet. Today a Manager
       * cannot reach Linked Accounts either, because Settings itself is Auditor and
       * above, so this keeps the same set of people.
       */
      IconComponent: BankIcon,
      name: "Accounts",
      url: "accounts",
      isVisible: byocOnly && isAuditorOrHigher && !isAuditorOrAccountant,
    },
    {
      IconComponent: MoneySend01Icon,
      name: "Payments",
      url: "payments",
      isVisible: showSpendMenus && featurePayments && isAuditorOrHigher,
    },
    {
      IconComponent: InvoiceIcon,
      name: "Get Paid",
      url: "get-paid",
      subItemDisplayType: "dropdown",
      subItemsAtRoot: true,
      isVisible: showSpendMenus && isAuditorOrHigher && anyGetPaidFeature,
    },
    {
      IconComponent: CreditCardIcon,
      name: "Cards",
      url: "cards",
      isVisible: showSpendMenus,
    },
    {
      IconComponent: LibraryIcon,
      name: "Receipt Library",
      url: "receipt-library",
      isVisible: showSpendMenus,
    },
    {
      IconComponent: Contact01Icon,
      name: "Contacts",
      url: "contacts",
      isVisible: showSpendMenus && featureContacts && isAuditorOrHigher,
    },
    {
      IconComponent: UserMultiple02Icon,
      name: "Users",
      url: "users",
      isVisible: isManagerOrHigher,
    },
    {
      IconComponent: Invoice03Icon,
      name: "Statements",
      url: "statements",
      isVisible: showSpendMenus && featureStatements && isAuditorOrHigher,
    },
    {
      IconComponent: AnalyticsUpIcon,
      name: "Analytics",
      url: "analytics",
      isVisible: isAuditorOrHigher,
    },
    {
      IconComponent: Settings02Icon,
      name: "Settings",
      url: "settings",
      isVisible: isAuditorOrHigher,
      subItemDisplayType: "drawer",
    },
  ];

  const subSideBarItems: SubNavItemConfig[] = [
    {
      name: "Payment Links",
      url: "payment-links",
      parentUrl: "get-paid",
      isVisible: showPaymentLinks,
    },
    {
      name: "Subscriptions",
      url: "subscriptions",
      parentUrl: "get-paid",
      isVisible: showSubscriptions,
    },
    {
      name: "Invoicing",
      url: "invoices",
      parentUrl: "get-paid",
      isVisible: featureInvoices,
    },
    {
      name: "Company Settings",
      group: "SettingsGroupCompany",
      url: "company-settings",
      parentUrl: "settings",
      isVisible: !isAuditorOrAccountant,
    },
    {
      name: "Branding",
      url: "branding",
      parentUrl: "settings",
      group: "SettingsGroupCompany",
      isVisible: showBrandingAndColors && !isAuditorOrAccountant,
    },
    {
      name: "Bank Settings",
      url: "bank-settings",
      parentUrl: "settings",
      group: "SettingsGroupCompany",
      // Two gates, not one: the page reads getListBankAccounts, which the
      // server holds at Auditor, and it is a management surface the read-only
      // roles do not get. Manager and Employee clear the second and fail the
      // first, so without the rank check they were offered a broken page.
      isVisible: isAuditorOrHigher && !isAuditorOrAccountant,
    },
    {
      name: "API Access",
      url: "api-access",
      parentUrl: "settings",
      group: "SettingsGroupConnections",
      isVisible: featureApiAccess && !isAuditorOrAccountant,
    },
    {
      name: "Custom Fields",
      group: "SettingsGroupCardsAndControls",
      url: "custom-fields",
      parentUrl: "settings",
      isVisible: featureCustomFields && !isAuditorOrAccountant,
    },
    {
      name: "Locations",
      group: "SettingsGroupCompany",
      url: "locations",
      parentUrl: "settings",
      isVisible: featureLocation,
    },
    {
      name: "Card Settings",
      group: "SettingsGroupCardsAndControls",
      url: "card-settings",
      parentUrl: "settings",
      isVisible: !isAuditorOrAccountant,
    },
    {
      name: "Allocations",
      group: "SettingsGroupCardsAndControls",
      url: "allocations",
      parentUrl: "settings",
      isVisible: featureAllocations,
    },
    {
      name: "Linked Accounts",
      group: "SettingsGroupConnections",
      url: "linked-accounts",
      parentUrl: "settings",
      // Hidden when Accounts is showing it at the top level, so BYOC-only
      // companies have one route to it rather than two.
      isVisible: byoc && !byocOnly && !isAuditorOrAccountant,
    },
    {
      name: "Card programs",
      group: "SettingsGroupConnections",
      url: "card-programs",
      parentUrl: "settings",
      isVisible: showSpendMenus && !isAuditorOrAccountant,
    },
    {
      name: "Integrations",
      group: "SettingsGroupConnections",
      url: "integrations",
      parentUrl: "settings",
      isVisible: featureIntegrations && !isAuditorOrAccountant,
    },
    {
      name: "Policies",
      group: "SettingsGroupCardsAndControls",
      url: featureTransactionPolicies
        ? "policies/transactions"
        : "policies/workflows",
      urlToMatch: "policies",
      parentUrl: "settings",
      isVisible: !isAuditorOrAccountant,
    },
    {
      name: t("notificationSettings.notifications"),
      group: "SettingsGroupNotifications",
      url: "notifications-settings",
      parentUrl: "settings",
      isVisible: featureNotification && !isAuditorOrAccountant,
    },
    {
      name: "Rewards",
      group: "SettingsGroupCompany",
      url: "rewards",
      parentUrl: "settings",
      isVisible: showRewards && !isAuditorOrAccountant,
    },
  ];

  return {
    sidebarItems: sidebarItems.filter((item) => item.isVisible !== false),
    subSideBarItems: subSideBarItems.filter((item) => item.isVisible !== false),
  };
}
